import {
  type RoomNameMatcherOptions,
  type RoomNameRatePlanQualifier,
  detectRatePlanQualifier,
  findMatchingRoomNames,
  findMostSimilarRoomName,
  isAllRoomsName,
  stripRoomNameNoise,
} from '../../shared/roomNameMatcher';

import { UNKNOWN_RATE_PLAN_NAME } from './egsRatePlans';

const EGS_MATCHER_OPTIONS: RoomNameMatcherOptions = { plurals: true };

// A score above this is shown to a CM for confirmation, never treated as a match.
export const EGS_SUGGESTION_MIN_SCORE = 0.5;

// Only `matched` carries `roomRateIds`, so nothing else can be applied by accident. "All Rooms"
// describes what the CM negotiated; it is not an instruction to surcharge every room rate.
export type EgsRoomMatch =
  | { matchedRoomTypes: Array<App.RoomType>; roomRateIds: Array<string>; source: 'matched' }
  | {
      /** Empty means we could not narrow the rooms, so every room rate is offered rather than none. */
      matchedRoomTypes: Array<App.RoomType>;
      qualifier: RoomNameRatePlanQualifier;
      qualifierText: string;
      source: 'rate_plan_qualified';
    }
  | { source: 'all_rooms' }
  | { source: 'suggested' }
  | { source: 'unmatched' };

export function getRoomRateIds(roomTypes: Array<App.RoomType>): Array<string> {
  return roomTypes.flatMap((roomType) => (roomType.room_rates ?? []).map((roomRate) => String(roomRate.id)));
}

function narrowRatePlanQualifiedRoomTypes(
  roomLabel: string,
  qualifierText: string,
  roomTypes: Array<App.RoomType>,
): Array<App.RoomType> {
  const candidateText = stripRoomNameNoise(roomLabel.replace(qualifierText, ' '));

  if (!candidateText) {
    return [];
  }

  const matchedNames = new Set(
    findMatchingRoomNames(
      candidateText,
      roomTypes.map((roomType) => roomType.name),
      EGS_MATCHER_OPTIONS,
    ),
  );

  return roomTypes.filter((roomType) => matchedNames.has(roomType.name));
}

export function matchEgsRoomLabel(roomLabel: string, roomTypes: Array<App.RoomType>): EgsRoomMatch {
  // Ahead of All Rooms: a board basis prices one rate plan, and every other verdict writes to all of them.
  const ratePlan = detectRatePlanQualifier(roomLabel);

  if (ratePlan) {
    return {
      matchedRoomTypes: narrowRatePlanQualifiedRoomTypes(roomLabel, ratePlan.text, roomTypes),
      qualifier: ratePlan.qualifier,
      qualifierText: ratePlan.text,
      source: 'rate_plan_qualified',
    };
  }

  // Before noise stripping, which would delete a parenthesised exclusion.
  if (isAllRoomsName(roomLabel)) {
    return { source: 'all_rooms' };
  }

  const candidateText = stripRoomNameNoise(roomLabel);

  if (!candidateText) {
    return { source: 'unmatched' };
  }

  const roomTypeNames = roomTypes.map((roomType) => roomType.name);
  const matchedNames = new Set(findMatchingRoomNames(candidateText, roomTypeNames, EGS_MATCHER_OPTIONS));

  if (matchedNames.size) {
    const matchedRoomTypes = roomTypes.filter((roomType) => matchedNames.has(roomType.name));

    return { matchedRoomTypes, roomRateIds: getRoomRateIds(matchedRoomTypes), source: 'matched' };
  }

  // The score gates whether rooms are offered at all; it is deliberately never shown to a CM.
  const suggestion = findMostSimilarRoomName(candidateText, roomTypeNames, EGS_MATCHER_OPTIONS);

  if (suggestion && suggestion.score >= EGS_SUGGESTION_MIN_SCORE) {
    return { source: 'suggested' };
  }

  return { source: 'unmatched' };
}

/**
 * Makes a new verdict a type error at every call site that forgot it, which `strictNullChecks` being off
 * otherwise allows. Returns the fallback rather than throwing, so a missed verdict renders a wrong label
 * instead of taking the tab down.
 */
export function unhandledEgsMatch<T>(match: never, fallback: T): T {
  return fallback;
}

// Which rooms a verdict reaches, derived here so no two readers of a verdict can disagree.
export interface EgsMatchScope {
  /** True where the room types are what the record says, so they can be counted. */
  isResolved: boolean;
  /** `every` where the record narrowed nothing, so the whole grid is reached rather than none of it. */
  roomTypeIds: Set<string> | 'every';
}

function toRoomTypeIds(roomTypes: Array<App.RoomType>): Set<string> {
  return new Set(roomTypes.map((roomType) => String(roomType.id)));
}

// Built per call rather than shared, so no caller can widen one verdict's rooms by mutating another's set.
function unmatchedScope(): EgsMatchScope {
  return { isResolved: false, roomTypeIds: new Set() };
}

export function getEgsMatchScope(match: EgsRoomMatch): EgsMatchScope {
  switch (match.source) {
    case 'all_rooms':
      return { isResolved: true, roomTypeIds: 'every' };

    case 'rate_plan_qualified':
      return {
        isResolved: false,
        roomTypeIds: match.matchedRoomTypes.length ? toRoomTypeIds(match.matchedRoomTypes) : 'every',
      };

    case 'matched':
      return { isResolved: true, roomTypeIds: toRoomTypeIds(match.matchedRoomTypes) };

    // A prompt, not a match: every room is reached, and none of them is counted.
    case 'suggested':
      return { isResolved: false, roomTypeIds: 'every' };

    case 'unmatched':
      return unmatchedScope();

    default:
      return unhandledEgsMatch(match, unmatchedScope());
  }
}

export type EgsRoomDataState = 'error' | 'loading' | 'unavailable' | 'ready';

export function getRoomDataState({
  error,
  hasRoomTypes,
  isLoading,
}: {
  error: unknown;
  hasRoomTypes: boolean;
  isLoading: boolean;
}): EgsRoomDataState {
  if (error) {
    return 'error';
  }

  if (isLoading) {
    return 'loading';
  }

  return hasRoomTypes ? 'ready' : 'unavailable';
}

// Null where a human has to choose the room first, rather than a fabricated zero.
export function getMatchRoomRateCount(match: EgsRoomMatch, roomTypes: Array<App.RoomType>): number | null {
  const { isResolved, roomTypeIds } = getEgsMatchScope(match);

  if (!isResolved) {
    return null;
  }

  const reached =
    roomTypeIds === 'every' ? roomTypes : roomTypes.filter((roomType) => roomTypeIds.has(String(roomType.id)));

  return getRoomRateIds(reached).length;
}

export function buildEgsMatchByRoomLabel(
  roomLabels: Array<string>,
  roomTypes: Array<App.RoomType>,
): Record<string, EgsRoomMatch> {
  return Object.fromEntries(
    Array.from(new Set(roomLabels)).map((roomLabel) => [roomLabel, matchEgsRoomLabel(roomLabel, roomTypes)]),
  );
}

export interface EgsRoomRateGroupItem {
  ratePlanName: string;
  roomRateId: string;
}

export interface EgsRoomRateGroup {
  roomRates: Array<EgsRoomRateGroupItem>;
  roomTypeId: string;
  roomTypeName: string;
}

// Carries ids alongside names because neither a rate plan name nor a room type name is unique, so
// only the ids are safe to key a list on.
export function buildRoomRateGroups(
  roomRateIds: Array<string>,
  roomTypes: Array<App.RoomType>,
): Array<EgsRoomRateGroup> {
  const wanted = new Set(roomRateIds);

  return roomTypes
    .map((roomType) => ({
      roomRates: (roomType.room_rates ?? [])
        .filter((roomRate) => wanted.has(String(roomRate.id)))
        .map((roomRate) => ({
          ratePlanName: roomRate.rate_plan?.name ?? UNKNOWN_RATE_PLAN_NAME,
          roomRateId: String(roomRate.id),
        })),
      roomTypeId: String(roomType.id),
      roomTypeName: roomType.name,
    }))
    .filter((group) => group.roomRates.length > 0);
}

export function getUnmatchedRoomRateIds({
  egsByRoomRateId,
  matchByRoomLabel,
}: {
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  matchByRoomLabel: Record<string, EgsRoomMatch>;
}): Array<string> {
  const matchedRoomRateIds = new Set(
    Object.values(matchByRoomLabel).flatMap((match) => (match.source === 'matched' ? match.roomRateIds : [])),
  );

  return Object.entries(egsByRoomRateId)
    .filter(([roomRateId, records]) => records.length > 0 && !matchedRoomRateIds.has(roomRateId))
    .map(([roomRateId]) => roomRateId);
}
